import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import cn from 'classnames';
import { Filter, FilterX, ListFilter } from 'lucide-react';
import { toast } from 'sonner';

import { useDispatch, useSelector } from 'react-redux';
import { selectUserToken } from '../redux/features/user/userSelectors';
import {
  selectSearchFilters,
  selectSearchQuery,
} from '../redux/features/dashboard/dashboardSelectors';
import { showSearch } from '../redux/features/dashboard/dashboardSlice';
import { useGetSearchRealtiesMapQuery } from '../redux/services/search/searchApi';
import { useIsMobile } from '../hooks/useMediaQuery';

import Map from '../components/ui/mapComponent/Map';
import { ErrorMsg } from '../components/ui/ErrorMsg';
import { IconButtonTemplate } from '../components/ui/buttons/IconButtonTemplate';
import { Dialog } from '../components/ui/Dialog';
import { FiltersMapForm } from '../components/form/forms/FiltersMapForm';
import { MapInfoButton } from '../components/ui/mapComponent/MapInfoButton';
import { MapActiveContextBar } from '../components/ui/mapComponent/MapActiveContextBar';
import { countFilterKeys } from '../components/ui/mapComponent/MapActiveContextBar/utils';
import { RealtyList } from '../components/ui/RealtyList';

import { colors, errorMessages } from '../data/constants';

export const MapPage = () => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  // Sync selected marker into ?highlight=. Called by Map on every activeMarker
  const handleActiveMarkerChange = id => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (id == null) next.delete('highlight');
        else next.set('highlight', String(id));
        return next;
      },
      { replace: true }
    );
  };
  const [isListingClick, setIsListingClick] = useState(false);
  const [isFiltersClick, setIsFiltersClick] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);

  const userToken = useSelector(selectUserToken);
  const searchFilters = useSelector(selectSearchFilters);
  const searchQuery = useSelector(selectSearchQuery);

  const {
    data: realtiesMap = [],
    isFetching: isRealtiesMapFetching,
    error: realtiesMapError,
  } = useGetSearchRealtiesMapQuery(
    {
      token: userToken,
      search_query: searchQuery ?? '',
      search_filters: searchFilters ?? '',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const filterCount = countFilterKeys(searchFilters);
  const hasFilters = filterCount > 0;

  const handleClearAppliedFilters = () => {
    dispatch(
      showSearch({
        isSearchVisible: true,
        searchQuery: searchQuery ?? '',
        searchFilters: '',
      })
    );
    setIsResetClicked(true);
  };

  const handleClearQuery = () => {
    dispatch(
      showSearch({
        isSearchVisible: true,
        searchQuery: '',
        searchFilters: searchFilters ?? '',
      })
    );
  };

  // One-shot "no results" toast per unique filter+query combination.
  const emptyToastKeyRef = useRef(null);
  useEffect(() => {
    if (isRealtiesMapFetching) return;
    const hasContext = hasFilters || Boolean(searchQuery);
    const key = `${searchFilters ?? ''}||${searchQuery ?? ''}`;
    if (realtiesMap.length === 0 && hasContext) {
      if (emptyToastKeyRef.current !== key) {
        emptyToastKeyRef.current = key;
        toast('No realties match your filters.');
      }
    } else {
      emptyToastKeyRef.current = null;
    }
  }, [
    isRealtiesMapFetching,
    realtiesMap,
    hasFilters,
    searchFilters,
    searchQuery,
  ]);

if (!isMobile) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return (
    <div className="relative flex pt-[54px] min-w-full w-screen h-screen">
      <Map
        realties={realtiesMap}
        highlightId={highlightId}
        onActiveMarkerChange={handleActiveMarkerChange}
        hideBottomSheet={isFiltersClick}
        isLoading={isRealtiesMapFetching}
      />

      <MapActiveContextBar
        searchQuery={searchQuery}
        searchFilters={searchFilters}
        onClearQuery={handleClearQuery}
        onClearFilters={handleClearAppliedFilters}
        onEditFilters={() => setIsFiltersClick(true)}
      />

      <div className="absolute top-28 right-2 flex flex-col gap-2 opacity-80 z-[2]">
        <IconButtonTemplate
          bgColor="border-whiteColor bg-whiteColor"
          handleClick={() => setIsListingClick(true)}
          icon={ListFilter}
          size={20}
        />

        <MapInfoButton />

        <IconButtonTemplate
          bgColor="border-whiteColor bg-whiteColor"
          handleClick={() => setIsFiltersClick(true)}
          icon={Filter}
          size={20}
          badgeCount={filterCount}
        />
        {hasFilters && (
          <IconButtonTemplate
            bgColor="border-red-200 bg-red-50"
            handleClick={handleClearAppliedFilters}
            icon={FilterX}
            size={20}
            color={colors.redColor}
            tooltipText="Clear applied filters"
          />
        )}
      </div>

      <div
        className={cn(
          'fixed inset-0 top-[54px] flex-col p-2 bg-black bg-opacity-40 z-[2]',
          { flex: isFiltersClick },
          { hidden: !isFiltersClick }
        )}
      >
        <FiltersMapForm
          handleCancel={() => setIsFiltersClick(false)}
          isResetClicked={isResetClicked}
          setIsResetClicked={setIsResetClicked}
        />
      </div>

      {realtiesMapError && <ErrorMsg message={errorMessages.data} />}

      {isListingClick && (
        <Dialog
          content={
            <div className="max-w-[314px] xs:max-w-[376px] h-[600px] overflow-y-auto overflow-hidden hide-scroll">
              <RealtyList isPwa={true} />
            </div>
          }
          classes={'max-w-[314px] xs:max-w-[376px] h-[600px]'}
          isOpen={isListingClick}
          onClose={() => setIsListingClick(false)}
        />
      )}
    </div>
  );
};
